import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    CircularProgress,
    Modal,
    Box,
    Typography,
    IconButton,
    Grid,
    FormControl,
    TextField,
    Alert,
    AlertTitle,
    useMediaQuery,
    useTheme,
    Fade,
    Backdrop,
    Paper,
    Divider,
    Chip,
    Select,
    MenuItem,
    InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DataTable from "./DataTable";
import ActivityQuestionForm from "../Activities/ActivityQuestionForm";
import MicroLearningForm from "./MicroLearningForm";
import DraftEditor from '../Assessments/DraftEditor';
import { addProfileImage, updateUserImage, getAllClients } from '../Queries';

const DataComponent = ({
    tabLabel,
    tableData,
    columns,
    loading,
    submitLoading,
    alert,
    activeTab,
    open,
    handleOpenModal,
    handleCloseModal,
    handleChange,
    handleSubmit,
    handleCloseAlert,
    registerData,
    editData,
    handleEdit,
    handleDelete: handleDeleteFromParent,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const formStyles = {
        textField: {
            '& .MuiOutlinedInput-root': {
                height: '44px',
                fontFamily: 'Poppins',
                fontSize: '14px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '& fieldset': {
                    borderColor: '#E5E7EB',
                    borderRadius: '8px',
                },
                '&:hover fieldset': {
                    borderColor: '#2563EB'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                    borderWidth: '1px'
                }
            },
            '& .MuiInputLabel-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                color: '#6B7280',
                fontWeight: 500,
                '&.MuiInputLabel-shrink': {
                    backgroundColor: '#fff',
                    padding: '0 8px'
                }
            },
            '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
                color: '#374151',
                letterSpacing: '0.2px'
            }
        },
        select: {
            '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                '& fieldset': {
                    borderColor: '#E5E7EB',
                    borderRadius: '8px',
                },
                '&:hover fieldset': {
                    borderColor: '#2563EB'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                    borderWidth: '1px'
                }
            },
            '& .MuiSelect-select': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                padding: '12px 16px',
                color: '#374151',
                letterSpacing: '0.2px'
            }
        },
        modalTitle: {
            fontFamily: 'Poppins',
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '24px'
        }
    };

    const [loadTableData, setLoadTableData] = useState([]);
    const [activityQuestions, setActivityQuestions] = useState([
        {
            activityKey: "question-activity",
            questionId: 1,
            title: "",
            questionText: "",
            options: [""],
            answerIndex: 0,
            explanation: "",
        },
    ]);
    const [activityTitle, setActivityTitle] = useState("");
    const [microLearningTopics, setMicroLearningTopics] = useState([]);
    const [client, setClient] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClientCode, setSelectedClientCode] = useState(registerData.client_code || '');
    const [selectedImage, setSelectedImage] = useState(registerData.image);
    const [imagePreview, setImagePreview] = useState(null);
    const [batchDescription, setBatchDescription] = useState('');
    const [formName, setFormName] = useState(registerData.name || '');
    const [formClientCode, setFormClientCode] = useState(registerData.client_code || '');
    
    // Evaluation Matrix types
    const evaluationMatrixTypes = [
        { value: "work_ethic", label: "Work Ethic" },
        { value: "skills", label: "Skills" },
        { value: "21_century_fluencies", label: "21st Century Fluencies" },
        { value: "learning_skill_growth", label: "Learning & Skill Growth" },
        { value: "task_ownership_delivery", label: "Task Ownership & Delivery" },
        { value: "problem_solving_thinking", label: "Problem-Solving & Thinking" },
        { value: "code_quality_engineering", label: "Code Quality & Engineering Practices" },
        { value: "communication_collaboration", label: "Communication & Collaboration" },
        { value: "professional_habits", label: "Professional Habits" }
    ];

    const chipStyles = {
        work_ethic: { bg: '#e3f2fd', color: '#1976d2' },
        skills: { bg: '#f3e5f5', color: '#7b1fa2' },
        '21_century_fluencies': { bg: '#ede7f6', color: '#5e35b1' },
        learning_skill_growth: { bg: '#fff8e1', color: '#f9a825' },
        task_ownership_delivery: { bg: '#fbe9e7', color: '#d84315' },
        problem_solving_thinking: { bg: '#e8f5e9', color: '#2e7d32' },
        code_quality_engineering: { bg: '#f3f3f3', color: '#455a64' },
        communication_collaboration: { bg: '#edeff3', color: '#3949ab' },
        professional_habits: { bg: '#e0f7fa', color: '#00838f' },
    };


    const handleClientChange = (event) => {
        const newClientCode = event.target.value;
        setSelectedClientCode(newClientCode);
        setFormClientCode(newClientCode);
    };

    const handleNameChange = (event) => {
        const newName = event.target.value;
        setFormName(newName);
        handleChange(event); // Call parent handler
    };

    const handleBatchDescriptionChange = useCallback((data) => {
        setBatchDescription(data);
    }, []);

    const fetchClients = async () => {
        try {
            const clientData = await getAllClients();
            setClients(clientData || []);
        } catch (error) {
            console.error('Error fetching clients:', error);
            setClients([]);
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    const clearImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        // Clear the file input
        const fileInput = document.getElementById('image-upload-input');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const getTableData = () => {
        let filteredData = [];
         if (activeTab === 1) {
            filteredData = tableData.filter((val) => val.type === "program_code");
        } else if (activeTab === 2) {
            filteredData = tableData.filter((val) => val.type === "registration_course");
        } else if (activeTab === 3) {
            filteredData = tableData.filter((val) => val.type === "activity");
        } else if (activeTab === 4) {
            const topics = tableData.filter((val) => val.type === "micro_learning_topics");
            setMicroLearningTopics(topics);
            filteredData = tableData.filter((val) => val.type === "micro_learning");
            let clientCodes = tableData.filter((val) => val.type === "program_code");
            setClient(clientCodes);
        } else if (activeTab === 5) {
            // Filter for Evaluation Matrix - combining all three types
            const allowedTypes = evaluationMatrixTypes.map(type => type.value);
            filteredData = tableData.filter(val => allowedTypes.includes(val.type));
            
        }
        setLoadTableData(filteredData);
    };

    const handleSubmitMicroLearning = (data) => {
        handleSubmit(data);
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();

        // If it's Client Code tab and image is selected, include image in submission
        if (tabLabel === "Client Code" && selectedImage) {
            try {
                const fileReader = new FileReader();
                fileReader.readAsDataURL(selectedImage);
                fileReader.onload = async () => {
                    const imageData = fileReader.result.split(',')[1]; // Extracting base64-encoded data
                    let response = await addProfileImage(`${formName}_logo.jpg`, imageData, "client_logo");
                    if (response.data && response.data.statusCode === 200) {
                        let result = JSON.parse(response.data.body);
                        setSelectedImage(result.imageUrl);
                        let data = {
                            component: "client_code",
                            name: formName,
                            image: result.imageUrl,
                            client_code: formClientCode
                        }
                        handleSubmit(data);
                    }
                };
            } catch (error) {
                alert("unable to upload image, please try again later!!")
                console.error('Error reading file:', error);
            }
        } else {
            let data = {
                component: tabLabel,
                name: formName,
                type: registerData.type,
                client_code: formClientCode,
                description: activeTab === 1 ? batchDescription : undefined
            }
            handleSubmit(data);
        }
    };

    useEffect(() => {
        setActivityQuestions(registerData.json_data || []);
        setActivityTitle(registerData.name || "");
        setSelectedImage(registerData.image);
        setImagePreview(registerData.image);
        setSelectedClientCode(registerData.client_code || '');
        
        // Update local form state
        setFormName(registerData.name || '');
        setFormClientCode(registerData.client_code || '');
        
        // Set batch description for batch code tab - only from json_data
        if (tabLabel === "Batch Code") {
            const desc = (registerData.json_data && registerData.json_data.description) ? registerData.json_data.description : '';
            setBatchDescription(desc);
        }
        
        getTableData();
        fetchClients();
    }, [tabLabel, open, editData, registerData.id, registerData.name, registerData.client_code, registerData.json_data, tableData]);

    // Clear image when modal closes
    useEffect(() => {
        if (!open) {
            clearImage();
        }
    }, [open]);

    const handleSave = () => {
        const data = {
            questions: activityQuestions,
            title: activityTitle,
        };
        handleSubmit(data);
        setActivityQuestions([
            {
                activityKey: "question-activity",
                questionId: 1,
                title: "",
                questionText: "",
                options: [""],
                answerIndex: 0,
                explanation: "",
            },
        ]);
    };

    const getModalTitle = () => {
        if (activeTab === 2) return "Activity Information";
        if (tabLabel === "Micro Learning") return "Micro Learning Video";
        if (tabLabel === "Client Code") return "Client Code Information";
        if (tabLabel === "Evaluation Matrix") return "Evaluation Matrix Information";
        return "Data Information";
    };

    const getModalIcon = () => {
        if (activeTab === 2) return "🎯";
        if (tabLabel === "Micro Learning") return "📹";
        if (tabLabel === "Client Code") return "🏢";
        if (tabLabel === "Evaluation Matrix") return "📊";
        return "📄";
    };

    // handleEdit is now passed from parent component

    const handleDelete = (rowId) => {
        const rowToDelete = loadTableData.find(row => row.row_id === rowId);
        
        if (rowToDelete && handleDeleteFromParent) {
            handleDeleteFromParent(rowToDelete);
        }
    };

    return (
        <Box sx={{
            bgcolor: 'white',
            minHeight: '100vh'
        }}>

            {/* Data Table */}
            <DataTable
                tableData={loadTableData}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                isLoading={loading}
                tabLabel={tabLabel}
                activeTab={activeTab}
                actionButtons={
                    <Button
                        variant="contained"
                        onClick={handleOpenModal}
                        startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                        size="small"
                        sx={{
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                            background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                            color: "#ffffff",
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.75,
                            minHeight: '36px',
                            "&:hover": {
                                background: 'linear-gradient(135deg, #0c0082 0%, #d63550 100%)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        Add {tabLabel}
                    </Button>
                }
            />

            {/* Enhanced Modal */}
            <Modal
                open={open}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Paper
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: isMobile ? "95%" : isTablet ? "85%" : "70%",
                            maxWidth: 700,
                            maxHeight: "90vh",
                            borderRadius: 3,
                            // boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                            outline: 'none',
                        }}
                    >
                        {/* Enhanced Modal Header */}
                        <Box
                            sx={{
                                p: 2.5,
                                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                color: 'white',
                                borderRadius: '8px 8px 0 0',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '2px',
                                    background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                                }
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h2" sx={{ fontSize: '1.2rem' }}>
                                        {getModalIcon()}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                                        {getModalTitle()}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            transform: 'rotate(90deg)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Modal Content */}
                        <Box sx={{
                            p: 2,
                            maxHeight: 'calc(90vh - 100px)',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#c1c1c1',
                                borderRadius: '3px',
                            },
                        }}>
                            {/* Loading State */}
                            {(loading || submitLoading) && (
                                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                    <Box textAlign="center">
                                        <CircularProgress sx={{ color: '#0a0068', mb: 2 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {submitLoading ? 'Submitting...' : 'Loading...'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Alert Section */}
                            {alert.open && (
                                <Box mb={3}>
                                    <Alert
                                        severity={alert.severity}
                                        onClose={handleCloseAlert}
                                        sx={{
                                            borderRadius: 2,
                                            '& .MuiAlert-message': {
                                                width: '100%'
                                            }
                                        }}
                                    >
                                        <AlertTitle sx={{ fontWeight: 600 }}>
                                            {alert.severity === "success" ? "Success" : "Error"}
                                        </AlertTitle>
                                        {alert.message}
                                    </Alert>
                                </Box>
                            )}

                            {/* Content based on tab */}
                            {!loading && !submitLoading && !alert.open && (
                                <>
                                    {activeTab === 2 ? (
                                        <ActivityQuestionForm
                                            questions={activityQuestions}
                                            setQuestions={setActivityQuestions}
                                            activityTitle={activityTitle}
                                            setActivityTitle={setActivityTitle}
                                            handleSubmit={handleSave}
                                        />
                                    ) : tabLabel === "Micro Learning" ? (
                                        <MicroLearningForm
                                            registerData={registerData}
                                            handleChange={handleChange}
                                            handleCloseModal={handleCloseModal}
                                            handleSubmit={handleSubmitMicroLearning}
                                            topics={microLearningTopics}
                                            editData={editData}
                                            clientCodes={clients}
                                        />
                                    ) : (
                                        <form onSubmit={handleFormSubmit}>
                                            <Grid container spacing={2}>
                                                {/* Name Field */}
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="Name"
                                                            type="text"
                                                            name="name"
                                                            value={formName}
                                                            onChange={handleNameChange}
                                                            required
                                                            size="small"
                                                            variant="outlined"
                                                            sx={formStyles.textField}
                                                        />
                                                    </FormControl>
                                                </Grid>

                                                {/* Client Dropdown - Show for all tabs except Client Code */}
                                                {tabLabel !== "Client Code" && (
                                                    <Grid item xs={12}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel id="client-select-label">Select Client</InputLabel>
                                                            <Select
                                                                labelId="client-select-label"
                                                                id="client-select"
                                                                value={formClientCode}
                                                                label="Select Client"
                                                                onChange={handleClientChange}
                                                                required
                                                                sx={formStyles.select}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Select a client...</em>
                                                                </MenuItem>
                                                                {clients.map((client) => (
                                                                    <MenuItem key={client.client_code} value={client.client_code}>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                                                🏢
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                {client.client_code}
                                                                            </Typography>
                                                                        </Box>
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                )}

                                                {/* Description Field for Batch Code */}
                                                {tabLabel === "Batch Code" && (
                                                    <Grid item xs={12}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                mb={1}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: 'text.primary',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    fontSize: '0.9rem'
                                                                }}
                                                            >
                                                                📄 Description
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block', mb: 1, fontSize: '0.75rem' }}
                                                            >
                                                                Provide a detailed description for this batch/program code
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius: 2,
                                                                    backgroundColor: 'rgba(10, 0, 104, 0.02)',
                                                                    '&:hover': {
                                                                        borderColor: '#cc4049',
                                                                    },
                                                                    '&:focus-within': {
                                                                        borderColor: '#0a0068',
                                                                        borderWidth: '2px',
                                                                        // boxShadow: '0 0 0 3px rgba(10, 0, 104, 0.1)',
                                                                    },
                                                                }}
                                                            >
                                                                <DraftEditor
                                                                    key={`batch-description-${registerData.id || 'new'}`}
                                                                    handleDescription={handleBatchDescriptionChange}
                                                                    description={batchDescription}
                                                                    clearEditor={!registerData.id && !batchDescription}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                )}

                                                {/* Type Field for Evaluation Matrix */}
                                                {tabLabel === "Evaluation Matrix" && (
                                                    <Grid item xs={12}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel id="evaluation-type-label">Type</InputLabel>
                                                            <Select
                                                                labelId="evaluation-type-label"
                                                                id="evaluation-type-select"
                                                                value={registerData.type || ""}
                                                                label="Type"
                                                                name="type"
                                                                onChange={handleChange}
                                                                required
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    backgroundColor: 'rgba(10, 0, 104, 0.02)',
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#cc4049',
                                                                        borderWidth: '2px',
                                                                    },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0a0068',
                                                                        borderWidth: '2px',
                                                                        // boxShadow: '0 0 0 3px rgba(10, 0, 104, 0.1)',
                                                                    },
                                                                }}
                                                            >
                                                                {evaluationMatrixTypes.map((type) => (
                                                                    <MenuItem key={type.value} value={type.value}>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <AssessmentIcon sx={{ fontSize: '1.2rem', color: '#0a0068' }} />
                                                                            {type.label}
                                                                        </Box>
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                )}

                                                {/* Enhanced Image Upload Section */}
                                                {tabLabel === "Client Code" && (
                                                    <Grid item xs={12}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                mb={1}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: 'text.primary',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    fontSize: '0.9rem'
                                                                }}
                                                            >
                                                                <ImageIcon sx={{ fontSize: '1rem' }} />
                                                                Upload Company Logo
                                                            </Typography>

                                                            <input
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                id="image-upload-input"
                                                                type="file"
                                                                onChange={handleImageChange}
                                                            />
                                                            <label htmlFor="image-upload-input">
                                                                <Button
                                                                    variant="outlined"
                                                                    component="span"
                                                                    startIcon={<CloudUploadIcon />}
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{
                                                                        textTransform: "capitalize",
                                                                        borderColor: "#cc4049",
                                                                        color: "#cc4049",
                                                                        borderRadius: 2,
                                                                        py: 1,
                                                                        borderStyle: 'dashed',
                                                                        borderWidth: 2,
                                                                        fontSize: '0.8rem',
                                                                        backgroundColor: 'rgba(204, 64, 73, 0.05)',
                                                                        "&:hover": {
                                                                            borderColor: "#0a0068",
                                                                            color: "#0a0068",
                                                                            bgcolor: "rgba(10, 0, 104, 0.05)",
                                                                            borderStyle: 'dashed',
                                                                            borderWidth: 2,
                                                                            transform: 'translateY(-1px)',
                                                                        },
                                                                        transition: 'all 0.2s ease-in-out',
                                                                    }}
                                                                >
                                                                    {imagePreview ? 'Change Image' : 'Choose Company Logo'}
                                                                </Button>
                                                            </label>

                                                            {/* Enhanced Image Preview */}
                                                            {imagePreview && (
                                                                <Fade in={true}>
                                                                    <Box mt={2}>
                                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                                            <Typography variant="caption" color="text.primary" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                                                Preview:
                                                                            </Typography>
                                                                            <Button
                                                                                size="small"
                                                                                onClick={clearImage}
                                                                                startIcon={<DeleteOutlineIcon />}
                                                                                sx={{
                                                                                    color: "#d32f2f",
                                                                                    textTransform: "capitalize",
                                                                                    fontSize: '0.75rem',
                                                                                    '&:hover': {
                                                                                        bgcolor: 'rgba(211, 47, 47, 0.04)'
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Remove
                                                                            </Button>
                                                                        </Box>
                                                                        <Paper
                                                                            elevation={2}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                textAlign: "center",
                                                                                bgcolor: "#fafafa",
                                                                                borderRadius: 2,
                                                                                border: '1px solid #e0e0e0'
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={imagePreview}
                                                                                alt="Company Logo Preview"
                                                                                style={{
                                                                                    maxWidth: "100%",
                                                                                    maxHeight: "120px",
                                                                                    objectFit: "contain",
                                                                                    borderRadius: '6px'
                                                                                }}
                                                                            />
                                                                            {selectedImage?.name && (
                                                                                <Typography variant="caption" display="block" mt={1} color="text.secondary">
                                                                                    {selectedImage.name}
                                                                                </Typography>
                                                                            )}
                                                                        </Paper>
                                                                    </Box>
                                                                </Fade>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>

                                            <Divider sx={{ my: 2 }} />

                                            {/* Enhanced Action Buttons */}
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        onClick={handleCloseModal}
                                                        size="small"
                                                        sx={{
                                                            textTransform: "capitalize",
                                                            borderColor: "#cc4049",
                                                            color: "#cc4049",
                                                            borderRadius: 2,
                                                            py: 1,
                                                            fontWeight: 600,
                                                            fontSize: '0.8rem',
                                                            backgroundColor: 'rgba(204, 64, 73, 0.05)',
                                                            "&:hover": {
                                                                borderColor: "#0a0068",
                                                                color: "#0a0068",
                                                                bgcolor: "rgba(10, 0, 104, 0.05)",
                                                                transform: 'translateY(-1px)',
                                                            },
                                                            transition: 'all 0.2s ease-in-out',
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        type="submit"
                                                        size="small"
                                                        sx={{
                                                            textTransform: "capitalize",
                                                            background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                                            color: "#ffffff",
                                                            borderRadius: 2,
                                                            py: 1,
                                                            fontWeight: 600,
                                                            fontSize: '0.8rem',
                                                            // boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                                                            "&:hover": {
                                                                background: 'linear-gradient(135deg, #0c0082 0%, #d63550 100%)',
                                                                // boxShadow: '0 4px 12px rgba(10, 0, 104, 0.4)',
                                                                transform: 'translateY(-1px)',
                                                            },
                                                            transition: 'all 0.2s ease-in-out',
                                                        }}
                                                    >
                                                        Save Changes
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </form>
                                    )}
                                </>
                            )}
                        </Box>
                    </Paper>
                </Fade>
            </Modal>
        </Box>
    );
};

export default DataComponent;